import React, { useState, useMemo } from 'react';
import { Check, ChevronsUpDown, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';

/**
 * Searchable investor picker — match by NAME or RP CODE (e.g. "18-00").
 * Renders as a button that opens a Popover with a fuzzy-search command list.
 *
 * Props:
 *   value         — currently selected investor id (or '' / 'all' for none)
 *   onChange(id)  — called with selected id ('all' for the All-Investors entry, if includeAll)
 *   investors     — list of { id, name, rp_code?, email? }
 *   includeAll    — show an "All Investors" entry at the top (default true)
 *   placeholder   — button label when nothing picked (default "Select investor…")
 *   allLabel      — label for the All entry (default "All Investors")
 *   className     — extra classes for the trigger button
 *   testId        — base data-testid (default "investor-picker")
 *   disabled      — disables the button
 *   clearIds      — optional Set (or array) of investor id/rp_code values
 *                   that have nothing outstanding — renders a green check
 *                   next to their name so an admin knows to skip them
 *                   (e.g. reconciliation's "nothing left to unmatch" tick)
 */
export function InvestorPicker({
  value,
  onChange,
  investors = [],
  includeAll = true,
  placeholder = 'Select investor…',
  allLabel = 'All Investors',
  className = '',
  testId = 'investor-picker',
  disabled = false,
  clearIds = null,
}) {
  const clearSet = useMemo(
    () => (clearIds ? (clearIds instanceof Set ? clearIds : new Set(clearIds)) : null),
    [clearIds]
  );
  const [open, setOpen] = useState(false);

  const selected = useMemo(() => {
    if (!value || value === 'all') return null;
    return investors.find(i => i.id === value || i.rp_code === value);
  }, [value, investors]);

  // Custom filter — match against name AND id/rp_code, case-insensitive,
  // tolerant of partial codes ("18" matches "18-00").
  const filterFn = (itemValue, search) => {
    if (!search) return 1;
    const q = search.toLowerCase();
    return itemValue.toLowerCase().includes(q) ? 1 : 0;
  };

  const label = (() => {
    if (!value || value === 'all') return includeAll ? allLabel : placeholder;
    if (selected) {
      const code = selected.rp_code || selected.id;
      return selected.name && selected.name !== code ? `${code} — ${selected.name}` : code;
    }
    return value;
  })();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={`justify-between font-normal ${className}`}
          disabled={disabled}
          data-testid={`${testId}-trigger`}
        >
          <span className="truncate">{label}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] min-w-[240px] max-w-[22rem] p-0"
        align="start"
      >
        <Command filter={filterFn} data-testid={`${testId}-command`}>
          <CommandInput
            placeholder="Search by name or RP code…"
            data-testid={`${testId}-input`}
          />
          <CommandList>
            <CommandEmpty>No investors found.</CommandEmpty>
            <CommandGroup>
              {includeAll && (
                <CommandItem
                  value="all all-investors"
                  onSelect={() => { onChange('all'); setOpen(false); }}
                  data-testid={`${testId}-item-all`}
                >
                  <Check className={`mr-2 h-4 w-4 ${(!value || value === 'all') ? 'opacity-100' : 'opacity-0'}`} />
                  <span className="font-medium">{allLabel}</span>
                </CommandItem>
              )}
              {investors.map(inv => {
                const code = inv.rp_code || inv.id;
                // Compose searchable text so filterFn sees BOTH code and name
                const searchable = `${code} ${inv.name || ''} ${inv.email || ''}`.trim();
                const isSelected = value === inv.id || value === inv.rp_code;
                const isClear = clearSet && (clearSet.has(inv.id) || clearSet.has(inv.rp_code));
                return (
                  <CommandItem
                    key={inv.id}
                    value={searchable}
                    onSelect={() => { onChange(inv.id); setOpen(false); }}
                    data-testid={`${testId}-item-${inv.id}`}
                  >
                    {/* Clear tick sits in a fixed-width slot on the far left so
                        it's always visible — on the right it got pushed out of
                        view by long names. Empty slot keeps names aligned. */}
                    {clearSet && (
                      <span className="mr-2 h-4 w-4 flex-shrink-0">
                        {isClear && (
                          <CheckCircle2
                            className="h-4 w-4 text-green-600"
                            data-testid={`${testId}-clear-${inv.id}`}
                          />
                        )}
                      </span>
                    )}
                    <Check className={`mr-2 h-4 w-4 flex-shrink-0 ${isSelected ? 'opacity-100' : 'opacity-0'}`} />
                    <div className="flex flex-col min-w-0 flex-1">
                      <span className="font-mono text-xs text-slate-500">{code}</span>
                      <span className="truncate text-sm">{inv.name || code}</span>
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default InvestorPicker;
